import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Users, ArrowRight } from "lucide-react";
import api, { resolveAssetUrl } from "@/lib/api";
import useSEO from "@/hooks/useSEO";

const VEH_HERO_BG =
  "https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?auto=format&fit=crop&w=2000&q=80";

const Vehicles = () => {
  useSEO({
    title: "Vehicles",
    description: "Premium vehicle rentals from Pune & Mumbai — Toyota Innova, Tempo Traveller, Fortuner, Mercedes-Benz coach, Volvo sleeper. Airport transfers, outstation, group tours.",
    path: "/vehicles",
  });
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/vehicles")
      .then((r) => setItems(r.data || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div data-testid="vehicles-page">
      <section className="relative h-[55vh] min-h-[380px] w-full overflow-hidden">
        <img src={VEH_HERO_BG} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 hero-overlay" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 h-full flex flex-col justify-end pb-14 text-white">
          <p className="text-xs uppercase tracking-[0.4em] opacity-80 mb-4 fade-up">— Our fleet</p>
          <h1 className="font-serif-display text-5xl sm:text-6xl lg:text-7xl leading-[1.0] fade-up-2">
            Vehicles
          </h1>
          <p className="mt-6 max-w-2xl text-base sm:text-lg opacity-90 fade-up-3 font-light">
            From quick airport transfers to multi-day mountain expeditions — our
            well-maintained fleet of cars, SUVs, mini-buses and luxury coaches
            is ready for every journey.
          </p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-12 mt-20 pb-8">
        {loading ? (
          <div className="text-center py-24 text-sm uppercase tracking-[0.25em] text-[var(--st-ink-2)]">
            Loading fleet…
          </div>
        ) : items.length === 0 ? (
          <div
            data-testid="vehicles-empty"
            className="border border-dashed border-[var(--st-border)] py-24 text-center"
          >
            <p className="font-serif-display text-3xl mb-3">No vehicles yet</p>
            <p className="text-sm text-[var(--st-ink-2)] mb-8">Sign in to add your fleet.</p>
            <Link
              to="/admin/login"
              data-testid="vehicles-empty-admin-link"
              className="inline-block px-6 py-3 text-xs uppercase tracking-[0.25em] btn-accent"
            >
              Sign in to add
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {items.map((v, idx) => (
              <VehicleCard key={v.id} v={v} idx={idx} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
};

const VehicleCard = ({ v, idx }) => (
  <article
    data-testid={`vehicle-${v.id}`}
    className="bg-[var(--st-surface)] border border-[var(--st-border)] overflow-hidden group flex flex-col"
    style={{ animation: `fadeUp 0.7s ease-out ${idx * 0.08}s both` }}
  >
    <div className="relative aspect-[4/3] overflow-hidden bg-[var(--st-bg)]">
      {v.image_url ? (
        <img
          src={resolveAssetUrl(v.image_url)}
          alt={v.name}
          className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
        />
      ) : (
        <div className="w-full h-full flex items-center justify-center text-[var(--st-ink-2)] text-xs uppercase tracking-[0.25em]">
          {v.name}
        </div>
      )}
      {v.seating && (
        <div className="absolute top-4 right-4 bg-[var(--st-ink)] text-white text-xs uppercase tracking-[0.2em] px-3 py-2 inline-flex items-center gap-1.5">
          <Users size={12} /> {v.seating}
        </div>
      )}
    </div>

    <div className="p-6 sm:p-7 flex-1 flex flex-col">
      {v.type && (
        <p className="text-[10px] uppercase tracking-[0.3em] text-[var(--st-accent)] mb-2">
          {v.type}
        </p>
      )}
      <h3 className="font-serif-display text-2xl sm:text-3xl leading-tight mb-3">
        {v.name}
      </h3>
      <p className="text-sm text-[var(--st-ink-2)] leading-relaxed mb-5 flex-1">
        {v.description}
      </p>

      {v.features?.length > 0 && (
        <div className="mb-5">
          <p className="text-[10px] uppercase tracking-[0.3em] text-[var(--st-ink-2)] mb-2">Features</p>
          <div className="flex flex-wrap gap-2">
            {v.features.map((f) => (
              <span
                key={f}
                className="text-[11px] tracking-wider px-3 py-1 border border-[var(--st-border)] text-[var(--st-ink)]"
              >
                {f}
              </span>
            ))}
          </div>
        </div>
      )}

      <Link
        to={`/contact?vehicle=${encodeURIComponent(v.name)}`}
        data-testid={`vehicle-enquire-${v.id}`}
        className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-[var(--st-ink)] border-b border-[var(--st-ink)] pb-1 self-start hover:text-[var(--st-accent)] hover:border-[var(--st-accent)] transition-colors"
      >
        Book / Enquire <ArrowRight size={14} />
      </Link>
    </div>
  </article>
);

export default Vehicles;
